
"use client"

import { useState, useEffect } from "react"
import styles from "./styles.module.css"
import { IoCloseSharp } from "react-icons/io5"
import Input from "../../modules/Input/Input"
import Select from "../../modules/Select/Select"
import Button from "../../modules/Button/Button"
import DatePicker from "../../../shared-modules/DatePicker/DatePicker"
import { parseDateSafe } from "../../../../utils/dateFormatters"
import {
  getBoxDeviceTypes,
  getDeviceTypes,
  getWarehouseStatuses,
  createWarehouseBox,
  updateWarehouseBox,
} from "../../../../services/inventory/detail"
import Spinner from "../../../spinner"

const BOX_STATUS_OPTIONS = [
  { label: "New", value: "new" },
  { label: "Refurbished", value: "refurbished" },
]

const INITIAL_BOX_STATE = {
  // case_manager: "John Doe",
  box_number: "",
  device_type: "",
  box_type: "",
  inventory_status: "In Warehouse",
  added_at: null,
}

const AddBox = ({ setBox, updateDetails, boxCount, handleBoxVisibility, onRefresh }) => {
  const [boxDetails, setBoxDetails] = useState(INITIAL_BOX_STATE)
  const [boxDeviceTypes, setBoxDeviceTypes] = useState([])
  const [allDeviceTypes, setAllDeviceTypes] = useState([])
  const [inventoryStatuses, setInventoryStatuses] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [dataLoading, setDataLoading] = useState(true)

  // 🔹 Autofill Case Manager from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user")
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        if (parsedUser?.name) {
          setBoxDetails((prev) => ({ ...prev, case_manager: parsedUser.name }))
        }
      } catch (e) {
        console.warn("Failed to parse user from localStorage", e)
      }
    }
  }, [])

  // 🔹 Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true)
      const [boxTypesData, deviceTypesData, statusesData] = await Promise.all([
        getBoxDeviceTypes(),
        getDeviceTypes(),
        getWarehouseStatuses(),
      ])

      if (boxTypesData) setBoxDeviceTypes(boxTypesData)
      if (deviceTypesData) setAllDeviceTypes(deviceTypesData)
      if (statusesData?.inventory_statuses) {
        setInventoryStatuses(statusesData.inventory_statuses.filter((status) => status !== "Distributed"))
      }
      setDataLoading(false)
    }
    fetchData()
  }, [])

  // 🔹 Set update details in form
  useEffect(() => {
    if (updateDetails) {
      setBoxDetails((prev) => ({
        ...prev,
        // case_manager: updateDetails.case_manager || prev.case_manager,
        box_number: updateDetails.box_number || "",
        device_type: updateDetails.device_type || "",
        box_type: updateDetails.box_type || "",
        inventory_status: updateDetails.inventory_status || "In Warehouse",
        added_at: updateDetails.added_at || null,
      }))
    }
  }, [updateDetails])

  // 🔹 Validation
  const validateForm = () => {
    const newErrors = {}
    if (!boxDetails.box_number.trim()) newErrors.box_number = "Box number is required"
    if (!boxDetails.device_type) newErrors.device_type = "Box device type is required"
    if (!boxDetails.box_type) newErrors.box_type = "Box status is required"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 🔹 Handle input changes
  const handleInputChange = (field, value) => {
    setBoxDetails((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }
  }

  const processBoxData = (result) => {
    const selectedBoxDeviceType = boxDeviceTypes.find((bdt) => bdt.id === boxDetails.device_type)
    return {
      ...result,
      // case_manager: boxDetails.case_manager,
      box_device_type_name: selectedBoxDeviceType?.name || "",
      added_at: boxDetails.added_at,
      compatible_device_types:
        selectedBoxDeviceType?.name === "Multiple"
          ? allDeviceTypes
          : selectedBoxDeviceType?.compatible_device_types || [],
    }
  }

  // 🔹 Add new box
  const addBoxHandler = async () => {
    if (!validateForm()) return

    setLoading(true)
    const payload = {
      box_number: boxDetails.box_number,
      box_type: boxDetails.box_type,
      device_type: boxDetails.device_type,
      inventory_status: boxDetails.inventory_status,
      ...(boxDetails.added_at && { added_at: parseDateSafe(boxDetails.added_at)?.toISOString() || new Date().toISOString() }),
    }

    const result = await createWarehouseBox(payload)
    if (result) {
      setBox(processBoxData(result))
      setBoxDetails(INITIAL_BOX_STATE)
      setErrors({})

      if (onRefresh) {
        onRefresh()
      }
    } else {
      setErrors({ submit: "Failed to create box. Please try again." })
    }
    setLoading(false)
  }

  // 🔹 Update box (with API)
  const updateBoxHandler = async () => {
    if (!validateForm()) return

    setLoading(true)
    const payload = {
      box_number: boxDetails.box_number,
      box_type: boxDetails.box_type,
      device_type: boxDetails.device_type,
      inventory_status: boxDetails.inventory_status,
      ...(boxDetails.added_at && { added_at: parseDateSafe(boxDetails.added_at)?.toISOString() || new Date().toISOString() }),
    }

    try {
      const result = await updateWarehouseBox(updateDetails.id, payload)
      if (result) {
        setBox(processBoxData(result))
        setErrors({})

        if (onRefresh) {
          onRefresh()
        }
      } else {
        setErrors({ submit: "Failed to update box. Please try again." })
      }
    } catch (error) {
      setErrors({ submit: "Error updating box. Please try again." })
    }
    setLoading(false)
  }

  if (dataLoading) {
    return (
      <section className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner scale={6} color="#10164D" />
          <p>Loading box configuration...</p>
        </div>
      </section>
    )
  }

  const boxDeviceTypeOptions = boxDeviceTypes.map((bdt) => ({
    label: bdt.name,
    value: bdt.id,
  }))

  return (
    <section className={styles.container}>
      {/* Header / Close */}
      <section className={styles.headerActionContainer}>
        {boxCount > 0 && (
          <button className={styles.dialogClose} onClick={() => handleBoxVisibility(false)}>
            <IoCloseSharp color="black" />
          </button>
        )}
      </section>

      {/* Fields */}
      <section className={styles.fieldsGrid}>
        <section className={styles.inputField}>
          <Input
            type="text"
            label="Tennovi Box Number *"
            name="box_number"
            value={boxDetails.box_number}
            onChange={(e) => handleInputChange("box_number", e.target.value)}
            placeholder="Enter box number"
            required
          />
          {errors.box_number && <span className={styles.error}>{errors.box_number}</span>}
        </section>

        <section className={styles.inputField}>
          <label className={styles.label}>Box Device Type *</label>
          <Select
            options={boxDeviceTypeOptions}
            value={boxDetails.device_type}
            onChange={(value) => handleInputChange("device_type", value)}
            placeholder="Select box device type"
            width="100%"
            searchable={false}
          />
          {errors.device_type && <span className={styles.error}>{errors.device_type}</span>}
        </section>

        <section className={styles.inputField}>
          <label className={styles.label}>Box Status *</label>
          <Select
            options={BOX_STATUS_OPTIONS}
            value={boxDetails.box_type}
            onChange={(value) => handleInputChange("box_type", value)}
            placeholder="Select box status"
            width="100%"
            searchable={false}
          />
          {errors.box_type && <span className={styles.error}>{errors.box_type}</span>}
        </section>

        <section className={styles.inputField}>
          <Input
            type="text"
            label="Inventory Status"
            name="inventory_status"
            value={boxDetails.inventory_status}
            readOnly
            placeholder="Prefilled inventory status"
          />
        </section>

        <section className={`${styles.inputField} ${styles.fullWidthField}`}>
          <label className={styles.label}>Added At (Optional)</label>
          <DatePicker
            selectedDate={boxDetails.added_at}
            onDateSelect={(date) => handleInputChange("added_at", date)}
            placeholder="Select date"
          />
        </section>
      </section>

      {errors.submit && <span className={styles.error}>{errors.submit}</span>}

      {/* Actions */}
      <section className={styles.actionContainer}>
        <Button
          variant="primary"
          size="small"
          onClick={updateDetails ? updateBoxHandler : addBoxHandler}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner scale={3} color="#ffffff" />
              <span>{updateDetails ? "Updating..." : "Creating..."}</span>
            </>
          ) : updateDetails ? (
            "Update Box"
          ) : (
            "Add Box"
          )}
        </Button>
      </section>
    </section>
  )
}

export default AddBox


